import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import SettingsBrightnessOutlinedIcon from "@mui/icons-material/SettingsBrightnessOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { useTranslation } from "react-i18next";
import { usePanelTheme } from "../../design-system/panel/PanelThemeProvider";
import { PANEL } from "../../design-system/panel/panelTokens";
import {
  panelDropdownMenuItemSx,
  panelDropdownPopoverModalProps,
  panelDropdownPopoverSlotProps,
  panelDropdownTriggerSx,
  panelTopbarActionIconSx,
} from "../../design-system/panel/panelDropdownSx";

const THEME_OPTIONS = [
  { id: "light", icon: LightModeOutlinedIcon, labelKey: "theme.light", hintKey: "theme.lightHint" },
  { id: "dark", icon: DarkModeOutlinedIcon, labelKey: "theme.dark", hintKey: "theme.darkHint" },
  { id: "system", icon: SettingsBrightnessOutlinedIcon, labelKey: "theme.system", hintKey: "theme.systemHint" },
];

function TriggerIcon({ preference, isDark }) {
  const sx = panelTopbarActionIconSx;
  if (preference === "system") return <SettingsBrightnessOutlinedIcon sx={sx} />;
  if (isDark) return <DarkModeOutlinedIcon sx={sx} />;
  return <LightModeOutlinedIcon sx={sx} />;
}

/** Panel topbar — açık / koyu / sistem (cihaz) görünüm menüsü */
export default function PanelThemeToggle() {
  const { t } = useTranslation("common");
  const { preference, setPreference, isDark } = usePanelTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    setAnchorEl(open ? null : event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (id) => {
    setPreference(id);
    handleClose();
  };

  return (
    <>
      <Tooltip title={t("theme.menu")} arrow>
        <IconButton
          onClick={handleOpen}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={t("theme.menu")}
          sx={panelDropdownTriggerSx}
        >
          <TriggerIcon preference={preference} isDark={isDark} />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        {...panelDropdownPopoverModalProps}
        slotProps={panelDropdownPopoverSlotProps(260)}
      >
        <Box sx={{ p: 1 }}>
          <Typography
            sx={{
              px: 1.25,
              pt: 0.5,
              pb: 1,
              fontSize: "0.68rem",
              fontWeight: 700,
              letterSpacing: "0.02em",
              textTransform: "none",
              color: PANEL.text.label,
            }}
          >
            {t("theme.menu")}
          </Typography>
          <Box component="ul" role="listbox" sx={{ m: 0, p: 0, listStyle: "none" }}>
            {THEME_OPTIONS.map((option) => {
              const selected = preference === option.id;
              const Icon = option.icon;
              return (
                <Box component="li" key={option.id} role="option" aria-selected={selected}>
                  <Button
                    fullWidth
                    onClick={() => handleSelect(option.id)}
                    aria-label={t(option.labelKey)}
                    sx={{
                      ...panelDropdownMenuItemSx(selected),
                      alignItems: "flex-start",
                      py: 1.15,
                    }}
                  >
                    <Icon sx={{ fontSize: 20, mt: 0.15, color: selected ? PANEL.accent.text : PANEL.text.muted }} />
                    <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: PANEL.text.primary, lineHeight: 1.25 }}>
                        {t(option.labelKey)}
                      </Typography>
                      <Typography sx={{ mt: 0.2, fontSize: "0.75rem", color: PANEL.text.muted, lineHeight: 1.35 }}>
                        {t(option.hintKey)}
                      </Typography>
                    </Box>
                    {selected ? (
                      <CheckRoundedIcon sx={{ fontSize: 18, mt: 0.2, color: PANEL.accent.text }} />
                    ) : (
                      <Box sx={{ width: 18 }} />
                    )}
                  </Button>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Popover>
    </>
  );
}
